// src/features/developers/components/DevelopersPage.tsx
//
// «Desarrolladores»: the documentation of the public API, in four tabs that are always these four
// and in this order. The tab is in the query string of the address, so a link opens the right one
// and a reload keeps it. The reference, the Postman collection and the OpenAPI file come from the
// document the API serves; the first tab and the samples are written by hand.
import React, { useMemo } from 'react';
import { useTranslate } from 'react-admin';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button } from '@mui/material';
import {
  CodeRounded as SamplesIcon,
  DownloadRounded as DownloadIcon,
  MenuBookRounded as ReferenceIcon,
  RocketLaunchRounded as StartIcon,
  VpnKeyRounded as KeysIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { SectionHeader } from '@/components/common/SectionHeader';
import { canOpenKeys, DEVELOPERS_KEYS_ROUTE, isDocumentationOnly } from '../access';
import { renderRecipes } from '../recipes';
import { searchOfTab, tabOfSearch } from '../tabs';
import type { DeveloperTab } from '../types';
import { useDeveloperDocs } from '../useDeveloperDocs';
import { environmentName, Pill, useCopy } from './common';
import { DownloadsTab } from './DownloadsTab';
import { OverviewTab } from './OverviewTab';
import { RecipesTab } from './RecipesTab';
import { ReferenceTab } from './ReferenceTab';

const TAB_ICONS: Record<DeveloperTab, React.ReactNode> = {
  overview: <StartIcon sx={{ fontSize: 18 }} />,
  reference: <ReferenceIcon sx={{ fontSize: 18 }} />,
  recipes: <SamplesIcon sx={{ fontSize: 18 }} />,
  downloads: <DownloadIcon sx={{ fontSize: 18 }} />,
};

const TAB_ORDER: DeveloperTab[] = ['overview', 'reference', 'recipes', 'downloads'];

export const DevelopersPage: React.FC = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const docs = useDeveloperDocs();
  const { copiedId, copy } = useCopy();
  const tab = tabOfSearch(location.search);
  const recipes = useMemo(() => renderRecipes(docs.spec), [docs.spec]);
  const t = (key: string, options?: Record<string, unknown>): string => translate(`developers.${key}`, options);

  const go = (next: DeveloperTab): void => {
    navigate({ pathname: location.pathname, search: searchOfTab(next, location.search) });
  };

  const current = docs.info?.environments.find((e) => e.key === docs.info?.currentEnvironment) ?? docs.info?.environments[0];

  if (docs.failure === 'forbidden') {
    return (
      <Box>
        <SectionHeader title={t('title')} sticky={false} />
        <Alert role="alert" severity="warning" variant="outlined" sx={{ mt: 2, maxWidth: 760 }}>
          {t('errors.forbidden')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 6, minWidth: 0 }}>
      <SectionHeader
        title={t('title')}
        subtitle={t('intro')}
        sticky={false}
        left={
          docs.info ? (
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
              <Pill>{t('pills.version', { version: docs.info.version })}</Pill>
              <Pill>{t('pills.operations', { smart_count: docs.info.operations })}</Pill>
              <Pill dot>{current ? environmentName(translate, current) : docs.info.currentEnvironment}</Pill>
            </Box>
          ) : undefined
        }
        rightActions={
          canOpenKeys(isDocumentationOnly(), docs.info?.canManageKeys) ? (
            <Button variant="outlined" size="small" startIcon={<KeysIcon />} onClick={() => navigate(DEVELOPERS_KEYS_ROUTE)}>
              {t('keys.open')}
            </Button>
          ) : undefined
        }
      />

      <Box
        role="tablist"
        aria-label={t('tabs.label')}
        sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', borderBottom: `1px solid ${theme.palette.divider}`, mt: 0.5, mb: 2.5 }}
      >
        {TAB_ORDER.map((key) => {
          const selected = tab === key;
          return (
            <Box
              key={key}
              component="button"
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => go(key)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                flex: 'none',
                border: 0,
                background: 'none',
                cursor: 'pointer',
                font: 'inherit',
                fontSize: '0.875rem',
                fontWeight: selected ? 600 : 500,
                px: 1.5,
                minHeight: 44,
                mb: '-1px',
                color: selected ? theme.palette.primary.dark : theme.palette.text.secondary,
                borderBottom: `2px solid ${selected ? theme.palette.primary.dark : 'transparent'}`,
                '&:hover': { color: selected ? theme.palette.primary.dark : theme.palette.text.primary },
                '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2, borderRadius: '6px' },
              }}
            >
              {TAB_ICONS[key]}
              {t(`tabs.${key}`)}
            </Box>
          );
        })}
      </Box>

      {docs.failure === 'unavailable' && (
        <Alert role="alert" severity="warning" variant="outlined" sx={{ mb: 3 }}>
          {t('errors.unavailable')}
        </Alert>
      )}

      <Box role="tabpanel" hidden={tab !== 'overview'}>
        <OverviewTab info={docs.info} recipes={recipes} baseUrl={docs.baseUrl} go={go} copiedId={copiedId} onCopy={copy} />
      </Box>
      <Box role="tabpanel" hidden={tab !== 'reference'}>
        <ReferenceTab active={tab === 'reference'} spec={docs.spec} title={docs.info?.title ?? t('title')} />
      </Box>
      <Box role="tabpanel" hidden={tab !== 'recipes'}>
        <RecipesTab recipes={recipes} baseUrl={docs.baseUrl} copiedId={copiedId} onCopy={copy} />
      </Box>
      <Box role="tabpanel" hidden={tab !== 'downloads'}>
        <DownloadsTab info={docs.info} specText={docs.specText} />
      </Box>
    </Box>
  );
};

export default DevelopersPage;
